import { useState } from 'react';
import { Share2, Facebook, Twitter, Copy, CheckCircle, MessageCircle, Mail } from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Textarea } from '../ui/textarea';

const SHARE_URL = 'https://owazamartialarts.com';
const FACEBOOK_URL = 'https://www.facebook.com/OwazaMartialArts/';

const DEFAULT_MESSAGE = "I train at Owaza Martial Arts in Spring Hill, FL! 🥋 Expert martial arts instruction for all ages. Check them out!";

const ShareExperience = () => {
  const [customMessage, setCustomMessage] = useState(DEFAULT_MESSAGE);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${customMessage}\n\n${SHARE_URL}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleFacebookShare = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}&quote=${encodeURIComponent(customMessage)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleTwitterShare = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(customMessage)}&url=${encodeURIComponent(SHARE_URL)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleSMSShare = () => {
    const body = `${customMessage}\n\n${SHARE_URL}`;
    window.location.href = `sms:?body=${encodeURIComponent(body)}`;
  };

  const handleEmailShare = () => {
    const subject = 'Check out Owaza Martial Arts!';
    const body = `${customMessage}\n\n${SHARE_URL}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/50 rounded-full mb-4">
            <Share2 className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Share Your Experience
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Help spread the word about martial arts training at Owaza!
          </p>
        </div>

        {/* Share Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            {/* Customizable Message */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Your Message
              </label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                className="min-h-[100px]"
                maxLength={280}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                {customMessage.length}/280 characters
              </p>
            </div>

            {/* Share Buttons */}
            <div className="space-y-3">
              {/* Facebook */}
              <Button
                onClick={handleFacebookShare}
                className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white"
              >
                <Facebook className="w-5 h-5 mr-3" />
                Share on Facebook
              </Button>

              {/* Twitter/X */}
              <Button
                onClick={handleTwitterShare}
                className="w-full bg-black hover:bg-gray-800 text-white"
              >
                <Twitter className="w-5 h-5 mr-3" />
                Share on X (Twitter)
              </Button>

              {/* SMS */}
              <Button
                onClick={handleSMSShare}
                variant="outline"
                className="w-full border-green-500 text-green-600 hover:bg-green-50"
              >
                <MessageCircle className="w-5 h-5 mr-3" />
                Share via Text Message
              </Button>

              {/* Email */}
              <Button
                onClick={handleEmailShare}
                variant="outline"
                className="w-full"
              >
                <Mail className="w-5 h-5 mr-3" />
                Share via Email
              </Button>

              {/* Copy Link */}
              <Button
                onClick={handleCopyLink}
                variant="outline"
                className="w-full"
              >
                {copied ? (
                  <>
                    <CheckCircle className="w-5 h-5 mr-3 text-green-600" />
                    <span className="text-green-600">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5 mr-3" />
                    Copy Message & Link
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4">
              Other Ways to Support Us
            </h3>
            <div className="space-y-3">
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 bg-[#1877F2] rounded-full flex items-center justify-center">
                  <Facebook className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Follow us on Facebook</p>
                  <p className="text-sm text-gray-500">Stay updated with the latest news</p>
                </div>
              </a>

              <a
                href="/review"
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">⭐</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Leave a Google Review</p>
                  <p className="text-sm text-gray-500">Share your training experience</p>
                </div>
              </a>

              <a
                href="/feedback"
                className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-xl">💬</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Give Us Feedback</p>
                  <p className="text-sm text-gray-500">Help us improve our programs</p>
                </div>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p className="font-semibold text-gray-700">Owaza Martial Arts</p>
          <p>14726 County Line Rd, Spring Hill, FL 34610</p>
          <p>(352) 428-0086</p>
        </div>
      </div>
    </div>
  );
};

export default ShareExperience;
